import { useCallback } from "react";
import { Document } from "models/schemas";
import { CreateDocumentFields } from "models/schemas";
import { FileMetadata } from "components/input-files/definitions";
import {
  RegisterDocumentRequest,
  RegisterDocumentResponse,
  UploadUrlRequest,
  UploadUrlResponse,
} from "components/create-audit-form/definitions";
import {
  DownloadUrlRequest,
  UpdateDocumentRequest,
  DeleteDocumentRequest,
} from "../documents-table/definitions";
import {
  getFileExtension,
  getFileNameWithoutExtension,
  getFileSize,
} from "utils/date-format";
import { StagedFile, WizardFileOperationsProps } from "./definitions";

export const useWizardFileOperations = (props: WizardFileOperationsProps) => {
  const {
    uploadUrlCall,
    registerDocCall,
    deleteDocCall,
    updateDocCall,
    lazyDownloadUrlCall,
    addNotification,
    teamName,
    currentUser,
    parentId,
    gaiaObjectId,
    category,
    stagedFiles,
    onStagedFilesChange,
    isCreateMode,
  } = props;

  const downloadSingleDoc = useCallback(
    async (stagedFile: StagedFile) => {
      try {
        if (stagedFile.originalDocument?.s3Prefix) {
          let request: DownloadUrlRequest = {
            documentId: stagedFile.originalDocument.documentId,
            preview: false,
          };
          const response = await lazyDownloadUrlCall(request).unwrap();

          if (response?.url) {
            window.location.href = response.url;
          }
        }
      } catch (e) {
        addNotification({
          type: "error",
          header: `File download failed.`,
          dismissible: true,
          timeout: 5000,
        });
      }
    },
    [lazyDownloadUrlCall, addNotification]
  );

  const downloadDocs = useCallback(
    async (selectedFiles: StagedFile[]) => {
      for (const stagedFile of selectedFiles) {
        if (stagedFile.originalDocument) {
          await downloadSingleDoc(stagedFile);
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    },
    [downloadSingleDoc]
  );

  const addStagedFile = useCallback(
    (file: File, fileName: string, documentType: string) => {
      const newStagedFile: StagedFile = {
        id: crypto.randomUUID(),
        file,
        fileName,
        documentType,
        isNew: true,
      };
      onStagedFilesChange([...stagedFiles, newStagedFile]);
    },
    [stagedFiles, onStagedFilesChange]
  );

  const addStagedLink = useCallback(
    (link: string, fileName: string, documentType: string) => {
      const newStagedFile: StagedFile = {
        id: crypto.randomUUID(),
        link,
        fileName,
        documentType,
        isNew: true,
      };
      onStagedFilesChange([...stagedFiles, newStagedFile]);
    },
    [stagedFiles, onStagedFilesChange]
  );

  const addMultipleStagedFiles = useCallback(
    (files: FileMetadata[]) => {
      const newStagedFiles: StagedFile[] = files.map((fileMetadata) => ({
        id: crypto.randomUUID(),
        file: fileMetadata.file,
        fileName: fileMetadata.fileName,
        documentType: fileMetadata.documentType,
        isNew: true,
      }));
      onStagedFilesChange([...stagedFiles, ...newStagedFiles]);
    },
    [stagedFiles, onStagedFilesChange]
  );

  const removeStagedFile = useCallback(
    (fileId: string) => {
      const fileToRemove = stagedFiles.find((f) => f.id === fileId);
      if (fileToRemove && !fileToRemove.isNew) {
        // Mark existing file for deletion instead of removing it
        const updatedFiles = stagedFiles.map((f) =>
          f.id === fileId ? { ...f, toDelete: true } : f
        );
        onStagedFilesChange(updatedFiles);
      } else {
        // Remove new staged file completely
        const updatedFiles = stagedFiles.filter((f) => f.id !== fileId);
        onStagedFilesChange(updatedFiles);
      }
    },
    [stagedFiles, onStagedFilesChange]
  );

  const updateStagedFileName = useCallback(
    (fileId: string, newFileName: string) => {
      const updatedFiles = stagedFiles.map((f) =>
        f.id === fileId ? { ...f, fileName: newFileName } : f
      );
      onStagedFilesChange(updatedFiles);
    },
    [stagedFiles, onStagedFilesChange]
  );

  const restoreStagedFile = useCallback(
    (fileId: string) => {
      const updatedFiles = stagedFiles.map((f) =>
        f.id === fileId ? { ...f, toDelete: false } : f
      );
      onStagedFilesChange(updatedFiles);
    },
    [stagedFiles, onStagedFilesChange]
  );

  // Function to process all staged files after successful wizard submission
  const processStagedFiles = useCallback(
    async (actualParentId: string) => {
      const results = {
        uploaded: 0,
        deleted: 0,
        errors: 0,
      };

      try {
        // Process new files (uploads)
        const newFiles = stagedFiles.filter((f) => f.isNew && !f.toDelete);
        for (const stagedFile of newFiles) {
          try {
            let uploadUrlResponse: UploadUrlResponse | null = null;

            if (stagedFile.file) {
              // Handle file upload
              let request: UploadUrlRequest = {
                prefix: category + "/" + actualParentId,
                fileName: stagedFile.file.name,
                teamName: teamName,
                securityLevel: "Normal",
              };
              uploadUrlResponse = await uploadUrlCall(request).unwrap();

              if (uploadUrlResponse) {
                await fetch(uploadUrlResponse.url, {
                  method: "PUT",
                  body: stagedFile.file,
                  headers: {
                    "x-amz-expected-bucket-owner":
                      uploadUrlResponse.bucketOwner,
                  },
                });
              }
            }

            // Register document
            const s3Prefix = uploadUrlResponse
              ? `${category}/${actualParentId}/${uploadUrlResponse.uniqueKey}/${
                  stagedFile.file!.name
                }`
              : "";

            const uploadDocRequest: RegisterDocumentRequest = {
              parentId: actualParentId,
              teamName: teamName,
              fileName: stagedFile.fileName,
              documentType: stagedFile.documentType,
              securityLevel: "Normal",
              s3Prefix: s3Prefix,
              externalUrl: stagedFile.link || "",
              extentionType: stagedFile.file
                ? getFileExtension(stagedFile.file.name)
                : "Link",
              ...(gaiaObjectId.startsWith("audit")
                ? { auditId: gaiaObjectId }
                : { gaiaObjectId: gaiaObjectId }),
              ...(stagedFile.file && {
                size: getFileSize(stagedFile.file.size),
              }),
            };

            await registerDocCall(uploadDocRequest).unwrap();
            results.uploaded++;
          } catch (e) {
            console.error("Failed to upload file:", stagedFile.fileName, e);
            results.errors++;
          }
        }

        // Process renamed files (existing files with changed names)
        const renamedFiles = stagedFiles.filter(
          (f) =>
            !f.isNew &&
            !f.toDelete &&
            f.originalDocument &&
            f.fileName !==
              getFileNameWithoutExtension(f.originalDocument.fileName)
        );

        for (const stagedFile of renamedFiles) {
          try {
            if (stagedFile.originalDocument) {
              const updateRequest: UpdateDocumentRequest = {
                documentId: stagedFile.originalDocument.documentId,
                fileName: stagedFile.fileName,
                documentType: stagedFile.documentType,
              };
              await updateDocCall(updateRequest).unwrap();
              results.uploaded++; // Count as successful operation
            }
          } catch (e) {
            console.error("Failed to update file:", stagedFile.fileName, e);
            results.errors++;
          }
        }

        // Process deletions
        const filesToDelete = stagedFiles.filter((f) => !f.isNew && f.toDelete);
        for (const stagedFile of filesToDelete) {
          try {
            if (stagedFile.originalDocument) {
              let request: DeleteDocumentRequest = {
                documentId: stagedFile.originalDocument.documentId,
              };
              await deleteDocCall(request).unwrap();
              results.deleted++;
            }
          } catch (e) {
            console.error("Failed to delete file:", stagedFile.fileName, e);
            results.errors++;
          }
        }

        // Show notifications
        const newFileCount = newFiles.length;
        const renamedFileCount = renamedFiles.length;

        if (newFileCount > 0) {
          addNotification({
            type: "success",
            header: `${newFileCount} file${
              newFileCount > 1 ? "s" : ""
            } uploaded successfully.`,
            dismissible: true,
            timeout: 5000,
          });
        }

        if (renamedFileCount > 0) {
          addNotification({
            type: "success",
            header: `${renamedFileCount} file${
              renamedFileCount > 1 ? "s" : ""
            } renamed successfully.`,
            dismissible: true,
            timeout: 5000,
          });
        }

        if (results.deleted > 0) {
          addNotification({
            type: "success",
            header: `${results.deleted} file${
              results.deleted > 1 ? "s" : ""
            } deleted successfully.`,
            dismissible: true,
            timeout: 5000,
          });
        }

        if (results.errors > 0) {
          addNotification({
            type: "error",
            header: `${results.errors} file operation${
              results.errors > 1 ? "s" : ""
            } failed.`,
            dismissible: true,
            timeout: 5000,
          });
        }

        return results;
      } catch (e) {
        addNotification({
          type: "error",
          header: "File operations failed.",
          dismissible: true,
          timeout: 5000,
        });
        return { uploaded: 0, deleted: 0, errors: stagedFiles.length };
      }
    },
    [
      stagedFiles,
      uploadUrlCall,
      registerDocCall,
      deleteDocCall,
      updateDocCall,
      teamName,
      category,
      gaiaObjectId,
      addNotification,
    ]
  );

  // Function to load existing files for edit mode
  const loadExistingFiles = useCallback(
    (documents: Document[]) => {
      const existingFiles: StagedFile[] = documents.map((doc) => ({
        id: doc.documentId,
        fileName: doc.fileName,
        documentType: doc.documentType,
        isNew: false,
        originalDocument: doc,
      }));
      onStagedFilesChange(existingFiles);
    },
    [onStagedFilesChange]
  );

  return {
    downloadSingleDoc,
    downloadDocs,
    addStagedFile,
    addStagedLink,
    addMultipleStagedFiles,
    removeStagedFile,
    updateStagedFileName,
    restoreStagedFile,
    processStagedFiles,
    loadExistingFiles,
  };
};
